import React from 'react';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import ChartLegend from '../../../components/charts/ChartLegend';
import colorsForCharts from '../colors';

type Props = {
  title: string;
  chartData: {
    labels: string[];
    datasets: {
      values: number[];
      color?: string[] | string;
    }[];
  };
};

type PieDataItem = {
  id: number;
  label: string;
  value: number;
  color: string;
};

const TaraPieChart = ({ title, chartData }: Props): JSX.Element => {
  const labels = Array.isArray(chartData?.labels) ? chartData.labels : [];
  const datasetValues: number[] = chartData?.datasets?.[0]?.values ?? [];
  const datasetColor: string[] | string | undefined =
    chartData?.datasets?.[0]?.color;

  // Use the API color array only if it's a valid array with proper length
  const useApiColorArray =
    Array.isArray(datasetColor) && datasetColor?.length === labels?.length;

  const dataset: PieDataItem[] = labels?.map((label: string, index: number) => {
    const value =
      typeof datasetValues[index] === 'number' ? datasetValues[index] : 0;
    const color = useApiColorArray
      ? datasetColor[index]
      : colorsForCharts[index % colorsForCharts?.length]; // fallback color

    return {
      id: index,
      label,
      value,
      color,
    };
  });

  return (
    <div className='pieChartComponent'>
      <h2 className='pieChartComponent__title'>{title || 'Pie Chart'}</h2>
      <div className='pieChartComponent__chart'>
        <div className='pieChartComponent__chart--container'>
          <div className='pieChartComponent__chart--container-chart'>
            {dataset?.length > 0 ? (
              <PieChart
                series={[
                  {
                    data: dataset?.map((item) => ({
                      id: item.id,
                      value: item.value,
                      label: item.label,
                      color: item.color,
                    })),
                    arcLabel: (item) => `${item?.value}`,
                    arcLabelMinAngle: 35,
                    arcLabelRadius: '60%',
                  },
                ]}
                margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                height={300}
                sx={{
                  [`& .${pieArcLabelClasses.root}`]: {
                    fontWeight: 'bold',
                  },
                }}
                slotProps={{
                  legend: {
                    hidden: true,
                  },
                }}
              />
            ) : (
              <p style={{ color: 'gray', fontSize: '0.9rem' }}>
                No data available to display.
              </p>
            )}
          </div>

          {dataset?.length > 0 && (
            <div className='pieChartComponent__chart--container-legend'>
              <div className='pieChartComponent__chart--container-legend-content'>
                {dataset?.map((item) => (
                  <ChartLegend
                    key={item.id}
                    color={item.color}
                    label={item.label}
                    value={item.value}
                    symbol=''
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaraPieChart;
